import React, { useState } from "react";
import { smsConversationsApi } from "../api/routes";
import { Button, Card, Input, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const formatEpoch = (value) => {
  if (!value && value !== 0) return "—";
  const raw = Number(value);
  if (Number.isNaN(raw)) return String(value);
  const ms = raw < 1000000000000 ? raw * 1000 : raw;
  return new Date(ms).toLocaleString();
};

export default function SmsConversationsPanel() {
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [panelState, setPanelState] = usePanelCache("smsConversations");
  const results = panelState.results || [];
  const showSearch = panelState.showSearch ?? true;

  const fetchByUser = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await smsConversationsApi.byUser(userId);
      const list = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
      setPanelState({ results: list, showSearch: false });
    } catch (e) {
      setPanelState({ results: [] });
      setToast({ message: e.message || "Error fetching conversation", tone: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card
        title="Conversations"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Button tone="ghost" onClick={() => setPanelState({ results: [] })}>Clear</Button>
            <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>
              {showSearch ? "Hide search" : "Show search"}
            </Button>
          </div>
        }
      >
        {showSearch && (
          <Card title="Search by user">
            <Input label="User ID" value={userId} onChange={setUserId} />
            <Button onClick={fetchByUser} disabled={!userId} loading={loading}>Search</Button>
          </Card>
        )}

        {results.length === 0 ? (
          <div style={{ padding: 12, color: "var(--stone-700)" }}>No conversations found. Search by user.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr style={{ background: "rgba(25,164,99,0.08)" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>User ID</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                  <th style={{ padding: 8, textAlign: "left" }}>State</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Starting Message</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Last Message</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Expires</th>
                </tr>
              </thead>
              <tbody>
                {results.map((conv) => (
                  <tr key={conv.userId} style={{ borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{conv.userId}</td>
                    <td style={{ padding: 8, fontWeight: 700 }}>{conv.status || "—"}</td>
                    <td style={{ padding: 8 }}>{conv.state || "—"}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{conv.startingMessageSk || "—"}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{conv.lastMessageSk || "—"}</td>
                    <td style={{ padding: 8, fontSize: 12 }}>{formatEpoch(conv.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
